import { match } from "ts-pattern";

import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";
import type { QuestionWithAnswer } from "@tycho/web-bindings/types";
import { CompletedActionCard } from "@/web/routes/_protected/smb/-dashboard/-CompletedActionCard";
import type { SmbActionDefinition } from "@/web/routes/_protected/smb/-dashboard/-types";

type Props = {
  question: QuestionWithAnswer;
};

type RenderedAnswer = {
  /** Headline answer — short enough to live in a `<p>` next to the question. */
  primary: string;
  /** Optional free-text note the user attached to their answer. */
  note?: string;
};

function renderAnswer(question: QuestionWithAnswer): RenderedAnswer {
  return match(question)
    .with({ tag: "YesNo" }, { tag: "NeedsMoreInfo" }, ({ value: q }) => {
      const a = q.answer;
      return a ? { primary: a.value ? "Yes" : "No", note: a.note } : { primary: "—" };
    })
    .with({ tag: "Number" }, ({ value: q }) => {
      const a = q.answer;
      if (!a) return { primary: "—" };
      const formatted = formatCompactWholeNumberWithUnit(formatNumberToShort(a.value), q.units.tag);
      return { primary: formatted, note: a.note };
    })
    .with({ tag: "MultipleChoice" }, ({ value: q }) => {
      const a = q.answer;
      if (!a) return { primary: "—" };
      // Selected answers are option ids — map them back to labels.
      const labelById = new Map(q.options.map((o) => [o.id, o.label]));
      const labels = a.value.map((id) => labelById.get(id) ?? id);
      return { primary: labels.length > 0 ? labels.join(", ") : "—", note: a.note };
    })
    .with({ tag: "Freeform" }, ({ value: q }) => {
      const a = q.answer;
      return a ? { primary: a.value, note: a.note } : { primary: "—" };
    })
    .exhaustive();
}

export function createUwQuestionCompleted(props: Props & { id?: string }): SmbActionDefinition {
  const { question, id = `uw-questions:done:${question.value.id}` } = props;
  const { primary, note } = renderAnswer(question);

  return {
    id,
    category: "budget",
    canSkip: false,
    render: () => (
      <CompletedActionCard
        category="budget"
        badge="Budget"
        title={question.value.title}
        details={{
          title: question.value.title,
          children: (
            <div className="flex flex-col gap-2 overflow-y-auto">
              {question.value.subtitle ? <p>{question.value.subtitle}</p> : null}
              <p className="opacity-70">Your answer:</p>
              <p className="font-medium">{primary}</p>
              {note ? <p className="opacity-70">"{note}"</p> : null}
            </div>
          ),
        }}
      />
    ),
  };
}
